/**
 * Custom hooks for home safety audit management
 * Uses React Query for data fetching and caching
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as homeAuditApi from '../api/homeAuditApi';
import type { SaveResponsesDto } from '../types/homeAudit.types';
export type { LatestCompletedSummary } from '../api/homeAuditApi';

// Query keys for React Query cache management
export const homeAuditKeys = {
  all: ['home-audit'] as const,
  active: () => [...homeAuditKeys.all, 'active'] as const,
  session: (id: string) => [...homeAuditKeys.all, 'session', id] as const,
  questions: () => [...homeAuditKeys.all, 'questions'] as const,
  completedSession: () => [...homeAuditKeys.all, 'completed'] as const,
  latestCompleted: () => [...homeAuditKeys.all, 'latest-completed'] as const,
};

/**
 * Fetch the most recent COMPLETED session summary for the options screen.
 * Returns data=null when backend 404s (no completed sessions).
 */
export const useLatestCompletedSession = () => {
  return useQuery({
    queryKey: homeAuditKeys.latestCompleted(),
    queryFn: homeAuditApi.getLatestCompletedSession,
    retry: false,
  });
};

/**
 * Fetch the active (IN_PROGRESS) session for the authenticated user.
 * Returns data=null when the backend responds 404 (treated as "no session")
 * Must set retry: false — 404 is not a transient error (Pitfall 1)
 */
export const useActiveSession = () => {
  return useQuery({
    queryKey: homeAuditKeys.active(),
    queryFn: homeAuditApi.getActiveSession,
    retry: false,
  });
};

/**
 * Fetch a session by id. Disabled when sessionId is empty/falsy.
 */
export const useSession = (sessionId: string) => {
  return useQuery({
    queryKey: homeAuditKeys.session(sessionId),
    queryFn: () => homeAuditApi.getSession(sessionId),
    enabled: !!sessionId,
  });
};

/**
 * Fetch the full question catalog (rarely changes — long staleTime ok, we omit for simplicity)
 */
export const useQuestions = () => {
  return useQuery({
    queryKey: homeAuditKeys.questions(),
    queryFn: homeAuditApi.getQuestions,
  });
};

/**
 * Create or resume a session (idempotent per user per D-05)
 *
 * Uses setQueryData (not invalidateQueries) so the active-session cache is
 * populated synchronously before navigate() fires in handleStart.  If we
 * only invalidated, RoomPage would mount with session=null (stale/error cache),
 * render <Navigate to="/home-audit" replace />, and bounce back — creating a
 * rapid back-and-forth render loop that exhausts CPU/memory and kills the tab.
 */
export const useCreateSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (stairsNa: boolean) => homeAuditApi.createOrResume(stairsNa),
    onSuccess: (session) => {
      // Populate cache immediately so RoomPage gets the session on first mount
      queryClient.setQueryData(homeAuditKeys.active(), session);
    },
  });
};

/**
 * Save a batch of per-room responses (D-04 per-room flush)
 * Invalidates session query so any open session-dependent view refetches.
 */
export const useSaveResponses = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, dto }: { sessionId: string; dto: SaveResponsesDto }) =>
      homeAuditApi.saveResponses(sessionId, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: homeAuditKeys.session(variables.sessionId) });
    },
  });
};

/**
 * Complete a session — triggers server-side scoring (SCR-04).
 * On success: cache the completed session id under homeAuditKeys.completedSession()
 * so the dashboard card can render "View Results" without a second endpoint (Pitfall 6).
 */
export const useCompleteSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => homeAuditApi.complete(sessionId),
    onSuccess: (data) => {
      queryClient.setQueryData(homeAuditKeys.completedSession(), data.id);
      queryClient.invalidateQueries({ queryKey: homeAuditKeys.all });
    },
  });
};
